// Drift velocity and acceleration for one device (issue #130).
//
// The drift chart above this panel shows how far a tower is from its baseline.
// This shows whether that distance is growing, and how fast — the honest version
// of the proposal's "predictive maintenance" claim, scoped to what the data
// supports.
//
// Three rules this component exists to keep, all of them from the issue:
//
//   1. **The window travels with every figure.** "0.02°/day" alone is not a
//      claim anyone can check. Every number here is rendered next to the window
//      it was measured over, and the window's actual dates are on screen. Since
//      #158 that extends to the *weather* window behind the temperature figures:
//      SamstWeather splices an ERA5 reanalysis and an Open-Meteo point forecast
//      at 2026-07-28, the two never ran in parallel, and an r computed across
//      that join is not the single measurement it looks like. When the window
//      crosses it, this panel says so next to the r.
//   2. **Nothing says or implies a failure date.** No remaining life, no
//      time-to-threshold, no countdown. There is a test asserting that no
//      rendered text matches that family of words.
//   3. **The flag is never coloured.** It is an uncalibrated threshold — no
//      device in this deployment has degraded, so there is no positive example
//      to set it against. Colour would give it an authority it has not earned;
//      the #118 coherence drop is the precedent. It renders in the same grey as
//      everything around it, with "uncalibrated" said out loud.
//
// A null is never rendered as a zero or a dash on its own: a figure the API
// declined to produce is shown with the reason it declined, because "not
// degrading" and "not measurable" are different answers.

import { useState, useEffect, useCallback } from "react";
import { generateFakeDriftTrend } from "../fakeData";
import HelpTip from "./HelpTip";

// Deliberately no polling. A 30-day velocity does not move in five minutes —
// the backend caches this response for exactly that reason — and the drift chart
// next to it already carries the live figures. It reloads when the device does.

const REASON_KEYS = {
  too_few_readings: "dtReasonTooFewReadings",
  sparse_coverage: "dtReasonSparseCoverage",
  no_fit: "dtReasonNoFit",
  no_velocity: "dtReasonNoVelocity",
  no_previous_window: "dtReasonNoPreviousWindow",
  insufficient_history: "dtReasonInsufficientHistory",
  degenerate_history_spread: "dtReasonDegenerateSpread",
  too_few_paired_bins: "dtReasonTooFewPairedBins",
};

// Human labels for the `source` values SamstWeather stores. An unrecognised value
// falls through to the raw string rather than being dropped: a source this build
// has never heard of is exactly the one worth showing.
const SOURCE_LABEL_KEYS = {
  "era5-archive": "dtSourceEra5",
  "open-meteo-forecast": "dtSourceLive",
  unlabelled: "dtSourceUnlabelled",
};

const signed = (v, dp = 4) =>
  !Number.isFinite(v) ? null : `${v > 0 ? "+" : v < 0 ? "−" : ""}${Math.abs(v).toFixed(dp)}`;

const shortDate = (iso) => {
  const d = new Date(iso);
  return isNaN(d) ? "—" : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
};

const hours = (ms) => (Number.isFinite(ms) ? Math.round(ms / 3600000) : "?");

const count = (n) => (Number.isFinite(n) ? n.toLocaleString("en-GB") : "?");

// One figure, its unit, and the window it describes. The window line is not
// optional — it is the difference between a measurement and an assertion.
function Figure({ label, value, unit, window: windowNote, detail, missing, help, t, simulated }) {
  return (
    <div style={styles.figure}>
      <span style={styles.figureLabel}>
        {label}
        {/* Attached to the figure, not to the provenance note below it: the
            note renders only when a temperature adjustment was possible, and
            the question "what temperature, measured how" is most pressing
            exactly when the panel says it could not adjust (#171). */}
        {help && <HelpTip topic={help} t={t} simulated={simulated} />}
      </span>
      {value === null ? (
        <span style={styles.figureMissing}>{missing}</span>
      ) : (
        <>
          <span style={styles.figureValue}>
            {value}
            <span style={styles.figureUnit}> {unit}</span>
          </span>
          <span style={styles.figureWindow}>{windowNote}</span>
        </>
      )}
      {detail && <span style={styles.figureDetail}>{detail}</span>}
    </div>
  );
}

export default function DegradationTrend({ deviceId, apiBaseUrl, t = (k) => k, simulated = false }) {
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTrend = useCallback(async () => {
    setLoading(true);
    try {
      let json;
      if (simulated) {
        json = generateFakeDriftTrend(deviceId);
      } else {
        const res = await fetch(
          `${apiBaseUrl}/api/drift/trend?device_id=${encodeURIComponent(deviceId)}`
        );
        if (!res.ok) throw new Error(`API error ${res.status}`);
        json = await res.json();
      }
      setTrend(json);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [deviceId, apiBaseUrl, simulated]);

  useEffect(() => { fetchTrend(); }, [fetchTrend]);

  if (loading) return <div style={styles.card}><p style={styles.placeholder}>{t("dtLoading")}</p></div>;
  if (error)
    return (
      <div style={styles.card}>
        <h3 style={styles.title}>{t("dtTitle")}</h3>
        <p style={styles.placeholder}>{t("dtError")}: {error}</p>
      </div>
    );
  if (!trend) return null;

  const days = trend.window_days;
  const overWindow = t("dtOverWindow", { days });
  const windowDates = t("dtWindowDates", {
    start: shortDate(trend.window_start),
    end: shortDate(trend.window_end),
  });
  const reason = (code) => (code ? t(REASON_KEYS[code] ?? code) : t("dtReasonUnknown"));

  const temp = trend.temperature ?? {};
  const tempWindow = trend.temperature_source?.window_summary ?? null;
  const flag = trend.flag ?? {};
  const fit = trend.fit ?? {};
  const axes = trend.axis_velocity_deg_per_day ?? {};

  // "Not raised" and "not assessed" are different answers and are worded as
  // such — flag.raised is null, not false, whenever the API could not decide.
  const flagText =
    flag.raised === true ? t("dtFlagRaised")
    : flag.raised === false ? t("dtFlagNotRaised")
    : `${t("dtFlagUnassessed")} — ${reason(flag.reason)}`;

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <h3 style={styles.title}>{t("dtTitle")}</h3>
        <span style={styles.headerWindow}>{`${overWindow} · ${windowDates}`}</span>
      </div>

      <div style={styles.figures}>
        <Figure
          label={t("dtVelocity")}
          value={signed(trend.velocity_deg_per_day)}
          unit={t("dtDegPerDay")}
          window={`${overWindow}, ${windowDates}`}
          missing={`${t("dtNotMeasurable")} — ${reason(trend.velocity_insufficient)}`}
        />
        <Figure
          label={t("dtAcceleration")}
          value={signed(trend.acceleration_deg_per_day_per_window)}
          unit={t("dtDegPerDay")}
          window={t("dtVsPrevious", {
            days,
            start: shortDate(trend.previous_window_start),
            end: shortDate(trend.previous_window_end),
          })}
          missing={`${t("dtNotMeasurable")} — ${reason("no_previous_window")}`}
        />
        <Figure
          label={t("dtAdjusted")}
          help="temperatureStats"
          t={t}
          simulated={simulated}
          value={temp.adjusted ? signed(temp.velocity_adjusted) : null}
          unit={t("dtDegPerDay")}
          window={`${overWindow}, ${windowDates}`}
          detail={
            temp.adjusted
              ? t("dtTempCoefficient", {
                  coef: signed(temp.coefficient_deg_per_c),
                  r: signed(temp.correlation, 2),
                  min: Number.isFinite(temp.min_c) ? temp.min_c.toFixed(1) : "?",
                  max: Number.isFinite(temp.max_c) ? temp.max_c.toFixed(1) : "?",
                })
              : null
          }
          missing={`${t("dtTempNotAdjusted")} — ${reason(temp.reason)}`}
        />
      </div>

      {/* Where the temperature behind the r came from. Rendered only where a
          temperature figure is actually shown — a provenance note attached to a
          number that is not on screen is noise. Not coloured: it is a caveat
          about what the figure is, not a severity. */}
      {temp.adjusted && tempWindow && (
        <p style={styles.note}>
          <span style={styles.noteLabel}>{t("dtTempSource")}</span>{" "}
          {t("dtTempSourceWindow", {
            start: shortDate(tempWindow.window.from),
            end: shortDate(tempWindow.window.to),
            sources:
              Object.keys(tempWindow.sources ?? {})
                .sort()
                .map((s) => (SOURCE_LABEL_KEYS[s] ? t(SOURCE_LABEL_KEYS[s]) : s))
                .join(", ") || "—",
          })}
          {tempWindow.crosses_source_boundary
            ? ` ${t("dtTempSourceCrosses", { at: shortDate(tempWindow.source_join_at) })}`
            : ` ${t("dtTempSourceSingle")}`}
        </p>
      )}

      {/* Per axis, because drift magnitude is a Euclidean norm: it cannot be
          negative, so it also rises when a mast oscillates harder without
          leaning anywhere. The signs are the only thing that separates the two. */}
      {["drift_x", "drift_y", "drift_z"].some((k) => Number.isFinite(axes[k])) && (
        <p style={styles.note}>
          <span style={styles.noteLabel}>{t("dtAxes")}</span>{" "}
          {["x", "y", "z"].map((axis, i) => (
            <span key={axis}>
              {i > 0 && "  ·  "}
              {t(`${axis}Axis`)} {signed(axes[`drift_${axis}`]) ?? "—"}
            </span>
          ))}{" "}
          {t("dtDegPerDay")} · {overWindow}
        </p>
      )}

      {/* The flag. No colour, by design — see the header comment. */}
      <p style={styles.note}>
        <span style={styles.noteLabel}>{t("dtFlag")}</span> {flagText}
        {" — "}
        <em style={styles.uncalibrated}>{t("dtFlagUncalibrated")}</em>
      </p>

      <p style={styles.note}>
        {t("dtFitNote", {
          bins: count(fit.n_bins_filled),
          total: count(fit.n_bins),
          span: hours(fit.bin_span_ms),
          readings: count(trend.n_readings),
          excluded: count(trend.n_excluded_no_angle_data),
        })}
      </p>

      <p style={styles.disclaimer}>{t("dtDisclaimer")}</p>
    </div>
  );
}

const styles = {
  card: {
    background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 8,
    padding: "16px 24px", marginTop: 16, maxWidth: 1600,
    fontFamily: "'Inter', system-ui, sans-serif",
  },
  header: { display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, flexWrap: "wrap", marginBottom: 12 },
  title: { margin: 0, fontSize: 15, fontWeight: 600, color: "#111827" },
  headerWindow: { fontSize: 12, color: "#6b7280" },
  figures: { display: "flex", gap: 24, flexWrap: "wrap", padding: "12px 16px", background: "#f9fafb", borderRadius: 6 },
  figure: { display: "flex", flexDirection: "column", gap: 2, minWidth: 190 },
  figureLabel: { fontSize: 11, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", display: "inline-flex", alignItems: "center", gap: 2 },
  // Neutral, like every other figure here. Nothing on this panel is coloured by
  // severity, because nothing on it has a calibrated severity.
  figureValue: { fontSize: 17, fontWeight: 600, color: "#111827", fontVariantNumeric: "tabular-nums" },
  figureUnit: { fontSize: 12, fontWeight: 500, color: "#6b7280" },
  figureWindow: { fontSize: 11, color: "#9ca3af" },
  figureDetail: { fontSize: 11, color: "#9ca3af" },
  figureMissing: { fontSize: 13, color: "#6b7280", fontStyle: "italic", lineHeight: 1.4 },
  note: { margin: "10px 0 0", fontSize: 11, color: "#6b7280", lineHeight: 1.6 },
  noteLabel: { textTransform: "uppercase", letterSpacing: "0.05em", color: "#9ca3af" },
  uncalibrated: { color: "#6b7280" },
  disclaimer: { margin: "12px 0 0", fontSize: 11, color: "#6b7280", lineHeight: 1.6, borderTop: "1px solid #f3f4f6", paddingTop: 10, fontStyle: "italic" },
  placeholder: { margin: 0, fontSize: 13, color: "#9ca3af" },
};
